/** Canonical English keys stored in D1 — never store translated labels as values. */

export const ANIMAL_TYPES = ['dog', 'cat', 'other'] as const
export type AnimalType = (typeof ANIMAL_TYPES)[number]

export const COLORS = [
  'black',
  'white',
  'brown_chocolate',
  'tan_fawn',
  'red_orange',
  'gray_blue',
  'cream_yellow',
  'gold_apricot',
] as const
export type ColorKey = (typeof COLORS)[number]

export const OWNERSHIP_STATUSES = [
  'confirmed_owned',
  'confirmed_abandoned',
  'no_known_owner',
  'reported_lost',
] as const
export type OwnershipStatus = (typeof OWNERSHIP_STATUSES)[number]

export const TEMPERAMENTS = [
  'not_sure',
  'aggressive',
  'friendly',
  'nervous',
  'timid',
] as const
export type Temperament = (typeof TEMPERAMENTS)[number]

export const GENDERS = ['unknown', 'male', 'female'] as const
export type Gender = (typeof GENDERS)[number]

/** Form / UI tri-state for fixed (spayed/neutered). Stored as NULL / 1 / 0. */
export const FIXED_OPTIONS = ['unknown', 'yes', 'no'] as const
export type FixedOption = (typeof FIXED_OPTIONS)[number]

export function fixedToSpayed(value: FixedOption): 0 | 1 | null {
  if (value === 'yes') return 1
  if (value === 'no') return 0
  return null
}

export function spayedToFixed(value: 0 | 1 | null | undefined): FixedOption {
  if (value === 1) return 'yes'
  if (value === 0) return 'no'
  return 'unknown'
}

export const SUPPORTED_LOCALES = ['en', 'es', 'pap', 'nl'] as const
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number]

/** Aruba approximate center */
export const ARUBA_CENTER = { lat: 12.5211, lng: -69.9683 }
/** Floor zoom; strict bounds often clamp higher on wide viewports. */
export const MIN_MAP_ZOOM = 8
/**
 * Requested initial zoom before bounds clamping. After the map settles, we bump
 * one level in from the clamped minimum so the island fills the view usefully.
 */
export const DEFAULT_ZOOM = MIN_MAP_ZOOM

/** Aruba plus a moderate coastal/ocean margin (minZoom 8 still usable). */
export const ARUBA_BOUNDS = {
  north: 12.77,
  south: 12.25,
  west: -70.2,
  east: -69.74,
}

export function isWithinArubaBounds(lat: number, lng: number): boolean {
  return (
    lat <= ARUBA_BOUNDS.north &&
    lat >= ARUBA_BOUNDS.south &&
    lng >= ARUBA_BOUNDS.west &&
    lng <= ARUBA_BOUNDS.east
  )
}

/**
 * Zoom out to the bounds-clamped minimum, then in one level.
 * Needed because strictBounds ignores DEFAULT_ZOOM when the viewport is wider
 * than the restricted region at that zoom.
 */
export function applyOverviewZoom(map: google.maps.Map) {
  map.setZoom(MIN_MAP_ZOOM)
  google.maps.event.addListenerOnce(map, 'idle', () => {
    const clamped = map.getZoom()
    if (clamped == null) return
    map.setZoom(clamped + 1)
  })
}
